import { useState } from "react";
import { Map as MapIcon } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import { useApplicationMutations } from "../../hooks";
import type { BulkGenerateFormatkiResult } from "../../types";
import { GenerateFormatkiModal } from "./GenerateFormatkiModal";

/**
 * „Wygeneruj formatki dla całego kompletu” — jedno kliknięcie zamiast wchodzenia
 * w każdy pakiet osobno. Szablon, skalę i #zmienne# wybiera człowiek w tym samym
 * modalu, którego używa pojedynczy pakiet (bez wskazania szablon dobiera się sam
 * z siatki: operator projektu × kubełek urzędowy).
 *
 * Każdy wniosek dostaje WŁASNY zestaw arkuszy — do wydruku spina się je potem
 * w jedną paczkę w kreatorze formatek, a system rozdziela arkusze z powrotem.
 */
export function BulkFormatkiAction({
  connectionId,
  applicationIds,
  label,
  variant = "secondary",
}: {
  connectionId: string;
  // puste = wszystkie aktywne wnioski lokalizacyjne projektu
  applicationIds?: string[];
  label: string;
  variant?: "primary" | "secondary" | "ghost";
}) {
  const { bulkFormatki } = useApplicationMutations();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [result, setResult] = useState<BulkGenerateFormatkiResult | null>(null);
  const [error, setError] = useState("");

  const start = (opts: {
    template?: string; scale_denominator?: number;
    field_values?: Record<string, string>; include_orientation?: boolean;
  }) => {
    setError("");
    bulkFormatki.mutate(
      { connection: connectionId, applications: applicationIds, ...opts },
      {
        onSuccess: (data) => { setResult(data); setPickerOpen(false); },
        onError: (e: unknown) => {
          const detail = (e as { response?: { data?: { errors?: { detail?: string } } } })
            ?.response?.data?.errors?.detail;
          setError(typeof detail === "string" ? detail : "Nie udało się uruchomić generowania.");
          setPickerOpen(false);
        },
      },
    );
  };

  return (
    <>
      <Button size="sm" variant={variant} onClick={() => setPickerOpen(true)}>
        <MapIcon size={13} className="mr-1" /> {label}
      </Button>
      {error && <div className="text-xs text-red-600">{error}</div>}

      {pickerOpen && (
        <GenerateFormatkiModal
          connectionId={connectionId}
          onClose={() => setPickerOpen(false)}
          onStart={start}
          starting={bulkFormatki.isPending}
        />
      )}

      {result && (
        <Modal open onClose={() => setResult(null)} title="Formatki dla kompletu" size="lg">
          <div className="space-y-3 text-sm">
            <p className="font-bold">{result.summary}</p>

            {result.started.length > 0 && (
              <div>
                <div className="section-label mb-1">Ruszyło ({result.started.length})</div>
                <div className="border border-[var(--color-border)]">
                  {result.started.map((row) => (
                    <div
                      key={row.application_id}
                      className="flex flex-wrap items-center gap-2 px-2 py-1 text-xs border-b border-[var(--color-border)] last:border-b-0"
                    >
                      <span className="font-mono">{row.number}</span>
                      <span className="flex-1 truncate">{row.office_name}</span>
                      <span className="text-[var(--color-text-muted)]">
                        {row.frames_count} ark.
                      </span>
                      {row.skipped_parcels.length > 0 && (
                        <span className="text-amber-700 dark:text-amber-400">
                          pominięte działki: {row.skipped_parcels.join(", ")}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {result.failed.length > 0 && (
              <div>
                <div className="section-label mb-1">Pominięte ({result.failed.length})</div>
                <div className="border border-[var(--color-border)]">
                  {result.failed.map((row) => (
                    <div
                      key={row.application_id}
                      className="flex flex-wrap items-center gap-2 px-2 py-1 text-xs border-b border-[var(--color-border)] last:border-b-0"
                    >
                      <span className="font-mono">{row.number}</span>
                      <span className="flex-1 truncate">{row.office_name}</span>
                      <span className="text-amber-700 dark:text-amber-400">{row.reason}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {result.started.length > 0 && (
              <div className="border-l-2 border-[var(--color-border-strong)] pl-2 text-xs space-y-1">
                <div className="font-bold">Co dalej</div>
                <p>
                  ZIP-y powstają w tle — pojawią się w „Znalezionych planach” w każdym
                  pakiecie (formatka tego wniosku stoi pierwsza i jest oznaczona).
                </p>
                <p>
                  Do wydruku spnij te zestawy w <b>jedną paczkę</b> w kreatorze formatek
                  (zakładka Mapa projektu): plotujesz i wgrywasz arkusze raz, a system
                  rozdzieli je z powrotem do właściwych pakietów. Grupy w paczce są
                  podpisane nazwą urzędu.
                </p>
              </div>
            )}

            <div className="flex justify-end">
              <Button onClick={() => setResult(null)}>Zamknij</Button>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}
